import { parseQuestionMeta } from './apiPayload';

export const QUESTION_BANK_TYPES = [
  { id: 3, type: 'Check Box' },
  { id: 4, type: 'Radio Button' },
  { id: 5, type: 'Drop Down' },
  { id: 10, type: 'Multiple Selection List Box' },
  { id: 11, type: 'Ranking' },
  { id: 12, type: 'Calculation' },
  { id: 13, type: 'Open-Ended' },
];

const COMPLEXITY_LABELS = { 1: 'Easy', 2: 'Medium', 3: 'High' };

export function complexityToLabel(value) {
  const num = Number(value);
  return COMPLEXITY_LABELS[num] || (typeof value === 'string' ? value : '');
}

export const DEFAULT_DROPDOWN_PLACEHOLDER = 'Select an option';

/** True when an option row is the dropdown placeholder (not a scored answer). */
export function isDropdownPlaceholderOption(opt) {
  if (!opt || opt.isCorrectOption) return false;
  const text = (opt.optionPrompt || '').trim();
  if (!text) return true;
  return /^select(\s+(an?\s+)?option|\s+options?)?$/i.test(text);
}

/** Ensure dropdown edit forms always have a dedicated placeholder row at index 0. */
export function normalizeDropdownOptionsForEdit(options = [], questionType) {
  if (Number(questionType) !== 5) return options || [];
  const opts = [...(options || [])];
  if (!opts.length) {
    return [{ optionPrompt: DEFAULT_DROPDOWN_PLACEHOLDER, isCorrectOption: false }];
  }
  if (!isDropdownPlaceholderOption(opts[0])) {
    opts.unshift({ optionPrompt: DEFAULT_DROPDOWN_PLACEHOLDER, isCorrectOption: false });
  }
  return opts;
}

export function getFilledOptions(options = [], questionType) {
  return (options || []).filter((opt, index) => {
    if (!opt?.optionPrompt?.trim()) return false;
    if (questionType === 5 && index === 0) return false;
    return true;
  });
}

export function buildRankingRanksFromCorrectOrder(options = [], correctOrder = '') {
  const ranks = String(correctOrder || '')
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean);
  const rankingRanks = {};
  (options || [])
    .map((opt, index) => ({ opt, index }))
    .filter(({ opt }) => opt?.optionPrompt?.trim())
    .forEach(({ index }, rankIndex) => {
      const rank = ranks[rankIndex];
      if (rank != null && rank !== '') {
        rankingRanks[index] = Number(rank);
      }
    });
  return rankingRanks;
}

export function buildCorrectOrderFromRanks(options = [], rankingRanks = {}) {
  return (options || [])
    .map((opt, index) => ({ opt, index }))
    .filter(({ opt }) => opt?.optionPrompt?.trim())
    .map(({ index }) => rankingRanks[index] ?? '')
    .join(',');
}

export function buildRankingQuestionMeta({ points, correctOrder }) {
  const meta = { type: 'ranking', points: Math.max(1, Number(points) || 1) };
  if (correctOrder) meta.correctOrder = correctOrder;
  return meta;
}

export function buildCalculationQuestionMeta({ points, showWork, workInstructions, correctAnswer }) {
  return {
    type: 'calculation',
    points: Math.max(1, Number(points) || 1),
    show_work: !!showWork,
    workInstructions: (workInstructions || '').trim(),
    correctAnswer: (correctAnswer || '').trim(),
  };
}

export function buildOpenEndedQuestionMeta({ points, correctAnswer }) {
  return {
    type: 'openEnded',
    points: Math.max(1, Number(points) || 1),
    correctAnswer: (correctAnswer || '').trim(),
  };
}

export function validateQuestionBankForm(data, questionType) {
  const type = Number(questionType ?? data?.questionType);
  if (!data?.skill?.value) return 'Please select a skill.';
  if (!data?.complexity) return 'Please select an expertise level.';
  if (!data?.questionPrompt?.trim()) return 'Please enter question text.';

  const options = data?.options || [];
  const filled = getFilledOptions(options, type);

  if (type === 11) {
    const rankedOptions = (options || [])
      .map((opt, index) => ({ opt, index }))
      .filter(({ opt }) => opt?.optionPrompt?.trim());
    if (rankedOptions.length < 2) return 'Ranking questions need at least two options.';
    const ranks = rankedOptions.map(({ index }) => data?.rankingRanks?.[index]);
    if (ranks.some((rank) => rank == null || rank === '')) {
      return 'Please assign a rank to every ranking option.';
    }
    if (new Set(ranks).size !== rankedOptions.length) {
      return 'Each ranking option must have a unique rank.';
    }
    return null;
  }

  if (type === 12) {
    if (filled.length < 2) return 'Calculation questions need at least two options.';
    const correctCount = filled.filter((opt) => opt.isCorrectOption).length;
    if (correctCount !== 1) return 'Please mark exactly one correct calculation option.';
    const correctOption = filled.find((opt) => opt.isCorrectOption);
    const correctAnswer = (data?.questionMeta?.correctAnswer || correctOption?.optionPrompt || '').trim();
    if (!correctAnswer) return 'Please provide a correct answer for the calculation question.';
    return null;
  }

  if (type === 13) {
    const correctAnswer = (data?.questionMeta?.correctAnswer || '').trim();
    if (!correctAnswer) return 'Please provide a correct answer for the open-ended question.';
    return null;
  }

  if (filled.length < 1) return 'Please add at least one answer option.';
  if (type === 4 && !filled.some((opt) => opt.isCorrectOption)) {
    return 'Please mark one correct answer option.';
  }
  return null;
}

export function buildQuestionBankSavePayload({ data, questionType, files, loggedUserID }) {
  const type = Number(questionType ?? data?.questionType);
  const options = [];
  (data?.options || []).forEach((item, index) => {
    if (!item.optionPrompt?.trim()) return;
    if (type === 5 && index === 0) return;
    options.push({
      OptionText: item.optionPrompt.trim(),
      IsCorrectOption: !!item.isCorrectOption,
    });
  });

  let questionMeta = null;
  let correctOrder = null;

  if (type === 11) {
    correctOrder = buildCorrectOrderFromRanks(data.options, data.rankingRanks);
    questionMeta = buildRankingQuestionMeta({
      points: data?.questionMeta?.points,
      correctOrder,
    });
  } else if (type === 12) {
    const filled = getFilledOptions(data.options, type);
    const correctOption = filled.find((opt) => opt.isCorrectOption);
    questionMeta = buildCalculationQuestionMeta({
      points: data?.questionMeta?.points,
      showWork: data?.questionMeta?.show_work ?? data?.questionMeta?.showWork,
      workInstructions: data?.questionMeta?.workInstructions,
      correctAnswer: data?.questionMeta?.correctAnswer || correctOption?.optionPrompt,
    });
  } else if (type === 13) {
    questionMeta = buildOpenEndedQuestionMeta({
      points: data?.questionMeta?.points,
      correctAnswer: data?.questionMeta?.correctAnswer,
    });
  }

  const payload = {
    loggedUserID,
    SkillId: Number(data?.skill?.value) || 0,
    Prompt: data?.questionPrompt?.trim(),
    questionType: type,
    Complexity: data?.complexity != null && data?.complexity !== '' ? String(data.complexity) : '',
    QuestionOption: options,
    questionId: data?.questionId != null && data?.questionId !== '' ? Number(data.questionId) : 0,
    ImageType: 'noImage',
    fileBytes: [],
    QuestionMeta: questionMeta,
    CorrectOrder: correctOrder,
  };

  if (type === 5 && data?.options?.[0]?.optionPrompt) {
    payload.PlaceholderText = data.options[0].optionPrompt;
  }

  if (files) {
    const splitData = files.split('base64,');
    if (splitData[1] != undefined) {
      payload.fileBytes = [splitData[1]];
      payload.ImageType = 'newImage';
    }
  }

  return payload;
}

const IMPORT_FIELD_MAP = {
  question: 'prompt',
  questiontype: 'questionType',
  complexity: 'complexity',
  skill_id: 'skill_Id',
  rightoptions: 'rightOptions',
  correctorder: 'correctOrder',
  correctanswer: 'correctAnswer',
  showwork: 'showWork',
  workinstructions: 'workInstructions',
  points: 'points',
  questionmeta: 'questionMeta',
  option1: 'option1',
  option2: 'option2',
  option3: 'option3',
  option4: 'option4',
  option5: 'option5',
  option6: 'option6',
};

export function mapExcelRowToImportPayload(row) {
  const obj = { savedStatus: 'Incorrect format' };
  Object.entries(row || {}).forEach(([key, value]) => {
    const mapped = IMPORT_FIELD_MAP[String(key).toLowerCase()];
    if (mapped) obj[mapped] = value;
  });
  return obj;
}

function normalizeImportQuestionType(value) {
  const raw = String(value ?? '').trim().toLowerCase();
  if (!raw) return '';
  const byName = {
    checkbox: '3',
    'check box': '3',
    radiobutton: '4',
    'radio button': '4',
    dropdown: '5',
    'drop down': '5',
    multiselect: '10',
    'multiple selection list box': '10',
    ranking: '11',
    calculation: '12',
    'open-ended': '13',
    'open ended': '13',
    openended: '13',
  };
  return byName[raw] || String(value).trim();
}

export function validateImportQuestionRow(row) {
  const questionType = normalizeImportQuestionType(row.questionType);
  if (!row.prompt?.toString().trim()) return 'Question text is required.';
  if (!questionType) return 'Question type is required.';
  if (!row.complexity?.toString().trim()) return 'Complexity is required.';
  if (row.skill_Id == null || row.skill_Id === '') return 'Skill_Id is required.';

  const options = [row.option1, row.option2, row.option3, row.option4, row.option5, row.option6].filter(
    (opt) => opt != null && String(opt).trim() !== ''
  );

  if (questionType === '11') {
    if (options.length < 2) return 'Ranking import needs at least two options.';
    if (!row.correctOrder?.toString().trim()) return 'CorrectOrder is required for ranking questions.';
    return null;
  }

  if (questionType === '12') {
    if (options.length < 2) return 'Calculation import needs at least two options.';
    const hasCorrect =
      row.correctAnswer?.toString().trim() ||
      row.rightOptions?.toString().trim();
    if (!hasCorrect) return 'CorrectAnswer or RightOptions is required for calculation questions.';
    return null;
  }

  if (questionType === '13') {
    if (!row.correctAnswer?.toString().trim()) {
      return 'CorrectAnswer is required for open-ended questions.';
    }
    return null;
  }

  if (!options.length) return 'At least one option is required.';
  return null;
}

export function normalizeImportRows(rows = []) {
  return rows.map((row) => ({
    ...row,
    questionType: normalizeImportQuestionType(row.questionType),
    prompt: row.prompt?.toString().trim(),
    complexity: row.complexity?.toString().trim(),
    skill_Id: row.skill_Id,
    correctOrder: row.correctOrder?.toString().trim(),
    correctAnswer: row.correctAnswer?.toString().trim(),
    showWork: row.showWork?.toString().trim(),
    workInstructions: row.workInstructions?.toString().trim(),
    points: row.points?.toString().trim(),
    rightOptions: row.rightOptions?.toString().trim().toLowerCase(),
    option1: row.option1,
    option2: row.option2,
    option3: row.option3,
    option4: row.option4,
    option5: row.option5,
    option6: row.option6,
  }));
}

export function summarizeQuestionBankImportResults(rows = []) {
  const list = Array.isArray(rows) ? rows : [];
  let savedCount = 0;
  let exactDuplicateCount = 0;
  let similarDuplicateCount = 0;
  let failedCount = 0;

  list.forEach((row) => {
    const status = String(row?.SavedStatus ?? row?.savedStatus ?? '').trim();
    if (status === 'Saved') savedCount += 1;
    else if (status === 'Already exists') exactDuplicateCount += 1;
    else if (status === 'Similar question exists') similarDuplicateCount += 1;
    else if (status) failedCount += 1;
  });

  const duplicateCount = exactDuplicateCount + similarDuplicateCount;
  const total = list.length;

  if (!total) {
    return {
      severity: 'error',
      message: '',
      errors: 'Import failed. No results returned from the server.',
      savedCount: 0,
      duplicateCount: 0,
      failedCount: 0,
    };
  }

  if (savedCount === total) {
    return {
      severity: 'success',
      message: `${savedCount} question${savedCount === 1 ? '' : 's'} imported successfully.`,
      errors: '',
      savedCount,
      duplicateCount: 0,
      failedCount: 0,
    };
  }

  const parts = [];
  if (savedCount > 0) {
    parts.push(`${savedCount} question${savedCount === 1 ? '' : 's'} imported`);
  }
  if (duplicateCount > 0) {
    const duplicateParts = [];
    if (exactDuplicateCount > 0) {
      duplicateParts.push(`${exactDuplicateCount} exact duplicate${exactDuplicateCount === 1 ? '' : 's'}`);
    }
    if (similarDuplicateCount > 0) {
      duplicateParts.push(`${similarDuplicateCount} similar duplicate${similarDuplicateCount === 1 ? '' : 's'}`);
    }
    parts.push(
      `${duplicateCount} duplicate question${duplicateCount === 1 ? '' : 's'} skipped (${duplicateParts.join(', ')})`
    );
  }
  if (failedCount > 0) {
    parts.push(`${failedCount} row${failedCount === 1 ? '' : 's'} failed validation`);
  }

  const summaryText = `${parts.join('. ')}.`;

  if (savedCount > 0) {
    return {
      severity: 'warning',
      message: summaryText,
      errors: '',
      savedCount,
      duplicateCount,
      failedCount,
    };
  }

  if (duplicateCount > 0 && failedCount === 0) {
    return {
      severity: 'warning',
      message: `All ${duplicateCount} duplicate question${duplicateCount === 1 ? '' : 's'} skipped. No new questions were inserted.`,
      errors: '',
      savedCount: 0,
      duplicateCount,
      failedCount: 0,
    };
  }

  return {
    severity: 'error',
    message: '',
    errors: summaryText || 'Import failed. No questions were inserted.',
    savedCount: 0,
    duplicateCount,
    failedCount,
  };
}

export function parseQuestionMetaForForm(question) {
  const meta =
    parseQuestionMeta(question?.questionMeta) ||
    parseQuestionMeta(question?.QuestionMeta) ||
    {};
  return {
    points: meta.points ?? 1,
    show_work: meta.show_work ?? meta.showWork ?? false,
    workInstructions: meta.workInstructions || meta.work_instructions || '',
    correctAnswer: meta.correctAnswer || meta.correct_answer || '',
    correctOrder: meta.correctOrder || meta.correct_order || question?.correctOrder || question?.CorrectOrder || '',
  };
}
